import { useState, type ReactNode } from "react";
import { LuChevronDown } from "react-icons/lu";
import { cn } from "@/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

/**
 * De-emphasized, collapsible wrapper for secondary phase info (roster,
 * result history, teasers). Keeps the primary action unmissable by default,
 * especially on mobile — details are one tap away, not competing for attention.
 */
interface HomeSecondarySectionProps {
  label: string;
  defaultOpen?: boolean;
  /** Optional always-visible summary shown even when collapsed. */
  summary?: ReactNode;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}

export function HomeSecondarySection({
  label,
  defaultOpen = false,
  summary,
  onOpenChange,
  children,
}: HomeSecondarySectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  return (
    <Collapsible open={open} onOpenChange={handleOpenChange}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex min-h-[44px] w-full items-center justify-between gap-2 py-1 text-left -mx-1 px-1 rounded-md transition-colors active:bg-accent/40"
        >
          <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {label}
            <LuChevronDown
              className={cn(
                "h-3.5 w-3.5 shrink-0 transition-transform",
                open && "rotate-180"
              )}
            />
          </span>
          {summary ? (
            <span className="shrink-0 text-xs text-muted-foreground">{summary}</span>
          ) : null}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-3">{children}</CollapsibleContent>
    </Collapsible>
  );
}
